import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  UNSAFE_future,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { NetworkStatusProvider } from "./context/NetworkStatusContext";

import { CartProvider } from "./hooks/useCart";
import { WishlistProvider } from "./hooks/useWishlist";
import ProtectedRoute from "./components/common/ProtectedRoute";
import Layout from "./components/layout/Layout";
import PublicLayout from "./components/layout/PublicLayout";
import PerformanceMonitor from "./components/common/PerformanceMonitor";
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";

import Profile from "./components/profile/Profile";
import MyOrders from "./components/profile/MyOrders";
import AdminPanel from "./components/admin/AdminPanel";
import UserManagement from "./components/admin/UserManagement";
import ProductManagement from "./components/admin/ProductManagement";
import OrderManagement from "./components/admin/OrderManagement";
import SalesAnalytics from "./components/admin/SalesAnalytics";
import NewsletterBuilder from "./components/admin/newsletter/NewsletterBuilder";
import NewsletterPreview from "./components/admin/newsletter/NewsletterPreview";
import AbandonedCarts from "./components/admin/AbandonedCarts";
import LostOrdersManagement from "./components/admin/LostOrdersManagement";
import CouponManagement from "./components/admin/CouponManagement";
import HomePage from "./components/common/HomePage";
import ShopPage from "./components/shop/ShopPage";
import SearchResultsPage from "./components/shop/SearchResultsPage";
import ProductDetailPage from "./components/shop/ProductDetailPage";
import CartPage from "./components/shop/CartPage";
import CheckoutPage from "./components/shop/CheckoutPage";
import CheckoutSuccessPage from "./components/shop/CheckoutSuccessPage";
import CheckoutPendingPage from "./components/shop/CheckoutPendingPage";
import CheckoutFailurePage from "./components/shop/CheckoutFailurePage";
import OrderTrackPage from "./components/shop/OrderTrackPage";
import OrderDetailsPage from "./components/shop/OrderDetailsPage";
import WishlistPage from "./components/shop/WishlistPage";
import About from "./components/About";
import Contact from "./components/Contact";
import FAQ from "./pages/FAQ";
import RefundPolicy from "./pages/RefundPolicy";
import Maintenance from "./pages/Maintenance";
import NotFound from "./components/common/NotFound";
import NotFoundPage from "./components/common/NotFoundPage";
import EmptyState from "./components/common/EmptyState";
import ThemedLoader from "./components/common/ThemedLoader";
import LightLoader from "./components/common/LightLoader";
import ErrorBoundary from "./components/common/ErrorBoundary";
import GoogleAnalytics from "./components/common/GoogleAnalytics";
import { logErrorToService } from "./utils/errorUtils";

import "./index.css";

function App() {
  const [isLoading, setIsLoading] = useState(false);

  // Check if maintenance mode is enabled
  const isMaintenanceMode =
    process.env.REACT_APP_MAINTENANCE_MODE === "true" ||
    process.env.REACT_APP_MAINTENANCE_MODE === "True" ||
    process.env.REACT_APP_MAINTENANCE_MODE === "TRUE";

  // Global error handlers for unhandled errors
  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      const error = event.reason || new Error("Unhandled Promise Rejection");
      logErrorToService(error, {
        componentName: "GlobalErrorHandler",
        errorType: "unhandledRejection",
        reason: event.reason?.toString() || "Unknown",
      }).catch(() => {
        // Silently fail if logging fails
      });
    };

    // Handle uncaught errors
    const handleError = (event) => {
      const error = event.error || new Error(event.message || "Uncaught Error");
      logErrorToService(error, {
        componentName: "GlobalErrorHandler",
        errorType: "uncaughtError",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }).catch(() => {
        // Silently fail if logging fails
      });
    };

    // Add event listeners
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    // Cleanup
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  // Register service worker for caching and offline functionality (production only)
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      if (process.env.NODE_ENV === "production") {
        window.addEventListener("load", () => {
          navigator.serviceWorker
            .register("/sw.js")
            .then((registration) => {
              console.log("SW registered: ", registration);
            })
            .catch((registrationError) => {
              console.log("SW registration failed: ", registrationError);
            });
        });
      } else {
        // Completely disable service worker in development
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (let registration of registrations) {
            registration.unregister();
          }
        });

        // Prevent service worker from registering in development
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: "SKIP_WAITING",
          });
        }
      }
    }
  }, []);

  // If maintenance mode is enabled, show only the maintenance page
  if (isMaintenanceMode) {
    return (
      <ErrorBoundary>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <div className="App">
            <GoogleAnalytics />
            <Routes>
              <Route path="*" element={<Maintenance />} />
            </Routes>
          </div>
        </Router>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <NetworkStatusProvider>
          <WishlistProvider>
            <CartProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <div className="App">
                  <GoogleAnalytics />
                  {/* Loading Screen - Only show for critical app loading */}
                  {isLoading && (
                    <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
                      <LightLoader size="xl" />
                    </div>
                  )}
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />

                    {/* Public layout routes (e-commerce style) */}
                    <Route path="/" element={<PublicLayout />}>
                      <Route index element={<HomePage />} />

                      <Route
                        path="profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="shop"
                        element={<ShopPage key="shop-all" />}
                      />
                      <Route
                        path="shop/accessories"
                        element={
                          <ShopPage
                            key="shop-accessories"
                            section="accessories"
                          />
                        }
                      />
                      <Route
                        path="shop/yarns"
                        element={
                          <ShopPage key="shop-yarns" section="yarns" />
                        }
                      />
                      <Route path="search" element={<SearchResultsPage />} />
                      <Route
                        path="products/:slug"
                        element={<ProductDetailPage />}
                      />
                      <Route path="cart" element={<CartPage />} />
                      <Route
                        path="wishlist"
                        element={
                          <ProtectedRoute>
                            <WishlistPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="checkout" element={<CheckoutPage />} />
                      <Route
                        path="checkout/success"
                        element={<CheckoutSuccessPage />}
                      />
                      <Route
                        path="checkout/pending"
                        element={<CheckoutPendingPage />}
                      />
                      <Route
                        path="checkout/failure"
                        element={<CheckoutFailurePage />}
                      />
                      <Route path="track" element={<OrderTrackPage />} />
                      <Route path="track/:token" element={<OrderTrackPage />} />
                      <Route
                        path="orders"
                        element={
                          <ProtectedRoute>
                            <MyOrders />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="orders/:orderId"
                        element={
                          <ProtectedRoute>
                            <OrderDetailsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="about" element={<About />} />
                      <Route path="contact" element={<Contact />} />
                      <Route path="faq" element={<FAQ />} />
                      <Route path="refund-policy" element={<RefundPolicy />} />
                    </Route>

                    {/* Admin routes */}
                    <Route path="/admin" element={<Layout />}>
                      <Route
                        index
                        element={
                          <ProtectedRoute adminOnly>
                            <AdminPanel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <ProtectedRoute adminOnly>
                            <UserManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="products"
                        element={
                          <ProtectedRoute adminOnly>
                            <ProductManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="orders"
                        element={
                          <ProtectedRoute adminOnly>
                            <OrderManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="sales"
                        element={
                          <ProtectedRoute adminOnly>
                            <SalesAnalytics />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="newsletter"
                        element={
                          <ProtectedRoute adminOnly>
                            <NewsletterBuilder />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="newsletter/preview"
                        element={
                          <ProtectedRoute adminOnly>
                            <NewsletterPreview />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="abandoned-carts"
                        element={
                          <ProtectedRoute adminOnly>
                            <AbandonedCarts />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="lost-orders"
                        element={
                          <ProtectedRoute adminOnly>
                            <LostOrdersManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="coupons"
                        element={
                          <ProtectedRoute adminOnly>
                            <CouponManagement />
                          </ProtectedRoute>
                        }
                      />
                    </Route>

                    {/* 404 routes */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>

                  {/* Toast notifications */}
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: "#363636",
                        color: "#fff",
                      },
                      success: {
                        duration: 3000,
                        iconTheme: {
                          primary: "#4ade80",
                          secondary: "#fff",
                        },
                      },
                      error: {
                        duration: 5000,
                        iconTheme: {
                          primary: "#ef4444",
                          secondary: "#fff",
                        },
                      },
                    }}
                  />
                </div>
              </Router>
            </CartProvider>
          </WishlistProvider>
        </NetworkStatusProvider>

        {/* Performance Monitor - Enable in development */}
        <PerformanceMonitor enabled={process.env.NODE_ENV === "development"} />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
